import { OrganizationInternalOwnerDTO, OrganizationInternalOwnersApi } from '@reposit/api-client';
import { AxiosResponse } from 'axios';
import { get } from 'lodash';
import { call, put, takeLatest } from 'redux-saga/effects';
import { FlashState } from '../../components/FlashMessage';
import { getErrorMessage } from '../../utils/common.utils';
import { syncEntitiesAndGetResults } from '../entities/entities.sagas';
import { FlashMessagesActionTypes, setFlashMessage } from '../flash-messages/flash-messages.actions';
import SCHEMA from '../schema';
import {
  createOrganizationAccountManagersApi,
  createOrganizationInternalOwnersApi,
  runSagaWithAuth,
} from '../utils/api.utils';
import {
  assignAccountManagerFailed,
  assignAccountManagerSuccess,
  ASSIGN_ACCOUNT_MANAGER_API_REQUESTED,
  ASSIGN_ACCOUNT_MANAGER_STORE_KEY,
  fetchOrganizationInternalOwnersByIdFailed,
  fetchOrganizationInternalOwnersByIdRequested,
  fetchOrganizationInternalOwnersByIdSuccess,
  FETCH_ORGANIZATION_INTERNAL_OWNERS_BY_ID_API_REQUESTED,
  OrganizationInternalOwnerActions,
  unassignAccountManagerFailed,
  unassignAccountManagerSuccess,
  UNASSIGN_ACCOUNT_MANAGER_API_REQUESTED,
  UNASSIGN_ACCOUNT_MANAGER_STORE_KEY,
} from './organization-internal-owner.actions';

// ****************
// WORKERS
// ****************

export function* fetchOrganizationInternalOwnersById({ payload }: { payload: { organizationId: string }; type: string }) {
  try {
    const api: OrganizationInternalOwnersApi = yield createOrganizationInternalOwnersApi();
    const apiResponse: AxiosResponse<OrganizationInternalOwnerDTO> = yield call(
      runSagaWithAuth(() => api.getOwners(payload.organizationId))
    );
    const ids: string[] = yield syncEntitiesAndGetResults(apiResponse.data, SCHEMA.organizationInternalOwners);
    yield put<OrganizationInternalOwnerActions>(fetchOrganizationInternalOwnersByIdSuccess(ids));
  } catch (e) {
    yield put<OrganizationInternalOwnerActions>(fetchOrganizationInternalOwnersByIdFailed(get(e, 'response.data.message', e)));
  }
}

export function* assignAccountManager({
  payload,
}: {
  payload: { organizationId: string; internalTeamMemberId: string };
  type: string;
}) {
  try {
    const api = yield createOrganizationAccountManagersApi();
    yield call(
      runSagaWithAuth(() =>
        api.assignAccountManager(payload.organizationId, { internalTeamMemberId: payload.internalTeamMemberId })
      )
    );
    yield put<OrganizationInternalOwnerActions>(assignAccountManagerSuccess());
    yield put(fetchOrganizationInternalOwnersByIdRequested(payload.organizationId));
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({
        key: ASSIGN_ACCOUNT_MANAGER_STORE_KEY,
        message: 'Success! Account manager updated.',
        state: FlashState.SUCCESS,
      })
    );
  } catch (e) {
    const error = getErrorMessage(e);
    yield put<OrganizationInternalOwnerActions>(assignAccountManagerFailed(error));
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({
        key: ASSIGN_ACCOUNT_MANAGER_STORE_KEY,
        message: error,
        state: FlashState.ERROR,
      })
    );
  }
}

export function* unassignAccountManager({ payload }: { payload: { organizationId: string }; type: string }) {
  try {
    const api = yield createOrganizationAccountManagersApi();
    yield call(runSagaWithAuth(() => api.unassignAccountManager(payload.organizationId)));
    yield put<OrganizationInternalOwnerActions>(unassignAccountManagerSuccess());
    yield put(fetchOrganizationInternalOwnersByIdRequested(payload.organizationId));
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({
        key: UNASSIGN_ACCOUNT_MANAGER_STORE_KEY,
        message: 'Success! Account manager unassigned.',
        state: FlashState.SUCCESS,
      })
    );
  } catch (e) {
    const error = getErrorMessage(e);
    yield put<OrganizationInternalOwnerActions>(unassignAccountManagerFailed(error));
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({
        key: UNASSIGN_ACCOUNT_MANAGER_STORE_KEY,
        message: error,
        state: FlashState.ERROR,
      })
    );
  }
}

// ****************
// WATCHERS
// ****************
export function* watchSagas() {
  yield takeLatest(FETCH_ORGANIZATION_INTERNAL_OWNERS_BY_ID_API_REQUESTED, fetchOrganizationInternalOwnersById);
  yield takeLatest(ASSIGN_ACCOUNT_MANAGER_API_REQUESTED, assignAccountManager);
  yield takeLatest(UNASSIGN_ACCOUNT_MANAGER_API_REQUESTED, unassignAccountManager);
}
